import * as React from 'react';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { AlertMessage } from '@common/react/components/UI/AlertMessage/AlertMessage';
import Button from '@common/react/components/Forms/Button';
import {
	dateValidator,
	emailValidator, lengthValidator,
	phoneRequiredValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import ScrollToError from '@common/react/components/Forms/ScrollToError';

import { Gender } from '@commonTuna/react/objects/Enums';
import { BaseInvite } from '@commonTuna/react/objects/BaseInvite';
import { PortalRegistrationStep } from '@commonTuna/react/objects/PortalRegistrationStep';

import { RegistrationUser, User, UserRole } from '@app/objects/User';
import { request } from '@app/components/Api';
import { Suffix } from '@app/objects/Suffix';
import { MarriageStatus } from '@app/objects/MarriageStatus';
import DemographicInfoRegistration from '@app/components/UI/DemographicInfo/DemographicInfoRegistration';
import { PatientLocation } from '@app/objects/PatientLocation';
import { PortalSettings } from '@app/components/Pages/Register/Register';

type FormValues = RegistrationUser | User;

interface ComponentProps {
	onSubmit: (response: UserRegistrationSteps) => void;
}

export interface UserRegistrationSteps {
	steps: Array<PortalRegistrationStep>;
	hippa: Array<BaseInvite>;
	forms: Array<BaseInvite>;
	user: User;
	portalSettings: PortalSettings;
}

interface ComponentState {
	isLoading: boolean;
	error: string;
}

const validationSchema = Yup.object().shape({
	firstName: lengthValidator(50, 'Must be not more than 50 characters').required(),
	lastName: lengthValidator(50, 'Must be not more than 50 characters').required(),
	emergencyContactName: lengthValidator(50, 'Must be not more than 50 characters'),
	emergencyContactRelation: lengthValidator(50, 'Must be not more than 50 characters'),
	birthDate: dateValidator,
	city: simpleStringValidator,
	region: simpleStringValidator,
	zip: simpleStringValidator,
	email: emailValidator,
	phoneNumber: phoneRequiredValidator,
	password: simpleStringValidator.min(8).required(),
	repeatPassword: Yup.string().required().oneOf([Yup.ref('password')], 'Repeat password should be the same as password'),
});

export const PatientMainForm: React.FC<ComponentProps> = ({ onSubmit }) => {
	const [state, setState] = React.useState<ComponentState>({
		isLoading: false,
		error: '',
	});

	const initialValues = {
		role: UserRole.User,
		suffix: Suffix.Mr,
		firstName: '',
		lastName: '',
		gender: Gender.Male,
		birthDate: null,
		marriageStatus: MarriageStatus.Single,
		email: '',
		phoneNumber: '',
		password: '',
		repeatPassword: '',
		address: '',
		city: '',
		region: '',
		zip: '',
		emergencyContactName: '',
		emergencyContactPhone: '',
		emergencyContactRelation: '',
		allowEmail: true,
		allowSms: true,
		allowCalls: true,
		allowPush: true,
		agreementForSignUp: true,
		captcha: '',
		patientLocations: [] as Array<PatientLocation>,
	} as RegistrationUser;

	const handleSubmit = (values: any) => {
		setState({ ...state, isLoading: true });

		request<UserRegistrationSteps>('userRegistrationAuth', values)
			.then((res) => {
				setState({ ...state, isLoading: false, error: '' });
				onSubmit(res);
			}).catch((err) => setState({ ...state, isLoading: false, error: err }));
	};

	const renderButtons = () => {
		return <>
			<div className="text-center button-box">
				<Button
					isLoading={state.isLoading}
					className="btn btn-component mb10"
				>
					Create Account
				</Button>
			</div>
		</>;
	};

	return (
		<>
			<div className="patient-register-page__form">
				<Formik
					validationSchema={validationSchema}
					initialValues={initialValues as FormValues}
					onSubmit={handleSubmit}
					render={(formikBag: FormikProps<FormValues>) => (
						<Form id="patient-registration-form">
							<ScrollToError {...formikBag} formId="patient-registration-form" />
							<DemographicInfoRegistration formikBag={formikBag} />
							{renderButtons()}
						</Form>
					)}
				/>
				<AlertMessage message={state.error} />
			</div>
		</>
	);
};
